import { useCallback, useEffect, useState } from "react";

/**
 * Theme preference hook.
 *
 * The actual theme is the `dark` class on `<html>`. The inline bootstrap script
 * in `public/index.html` sets it before React mounts: on `/bulandi-2026` it uses
 * saved choice -> `prefers-color-scheme` fallback; elsewhere it forces light.
 *
 * This hook keeps a React state mirror of that class, persists changes to
 * `localStorage`, and listens for cross-tab updates via the `storage` event.
 */

const STORAGE_KEY = "theme";

const isBrowser = typeof window !== "undefined" && typeof document !== "undefined";

const readDocumentTheme = () => {
  if (!isBrowser) return "light";
  return document.documentElement.classList.contains("dark") ? "dark" : "light";
};

export const BULANDI_AUTO_THEME_PATH = "/bulandi-2026";

export const usesBulandiAutoTheme = (pathname) =>
  pathname === BULANDI_AUTO_THEME_PATH;

export const resolveStoredTheme = () => {
  if (!isBrowser) return "light";
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    const prefersDark =
      window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches;
    if (saved === "light") return "light";
    if (saved === "dark") return "dark";
    return prefersDark ? "dark" : "light";
  } catch (_err) {
    return "light";
  }
};

export const applyDocumentTheme = (next) => {
  if (!isBrowser) return;
  const root = document.documentElement;
  if (next === "dark") {
    root.classList.add("dark");
  } else {
    root.classList.remove("dark");
  }
  root.dataset.theme = next;
};

export function useThemePreference() {
  const [theme, setThemeState] = useState(readDocumentTheme);

  const setTheme = useCallback((nextRaw) => {
    const next = nextRaw === "dark" ? "dark" : "light";
    applyDocumentTheme(next);
    if (isBrowser) {
      try {
        window.localStorage.setItem(STORAGE_KEY, next);
      } catch (_err) {
        // Ignore storage failures (private mode, quota, etc.).
      }
    }
    setThemeState(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(theme === "dark" ? "light" : "dark");
  }, [setTheme, theme]);

  useEffect(() => {
    if (!isBrowser) return undefined;

    const handleStorage = (event) => {
      if (event.key !== STORAGE_KEY) return;
      const next = event.newValue === "dark" ? "dark" : "light";
      applyDocumentTheme(next);
      setThemeState(next);
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  return { theme, isDark: theme === "dark", setTheme, toggleTheme };
}

export default useThemePreference;
